import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ApiConnector, RequestQueryPayload, Table } from '@cureskin/api-client';
import { ConnectionService } from '../../../../services/connection-service';
import { AppConfig } from '../../../app.config';
import { CatalogVariantModal } from './catolog-variant-modal';
import { Broadcaster } from '../../../../components/broadcaster';
import { FieldVisibilityService } from '../../../../services/field-visibility-service/field-visibility.service';
import { CatalogAddOnInstructionEditModal } from './catalog-addon-instruction-edit-modal';

@Component({
    selector: 'catalog-view',
    templateUrl: './catalog-view.html',
    styleUrls: ['./catalog-view.scss'],
    standalone: false
})
export class CatalogViewComponent implements OnDestroy {
  productObj: any;
  variantObj: Array<any>;
  sampleObj: Array<any>;
  showInactive: Boolean;
  regimens: any;
  ui: any;
  restrictions: Array<any>;
  sideEffects: Array<any>;
  indicationRestrictions: Array<any>;
  subscriptions: Array<Subscription>;
  data: any;
  articles: any;
  user: any;
  embeddedLink: any;
  iframeLink: SafeResourceUrl;
  regimenPanelOpen: boolean = false;
  comboPanelOpen: boolean = false;
  combos: Array<Table.Catalog> = [];
  multipleValueFields: Array<{ title: string, fieldName: string }>;
  articleImageSrc: string;
  selectedLanguage: string = 'en';
  articleImageObj: any;
  articleImageIds: Array<string> = [];
  selectedArticleImageIndex: number = 0;
  constructor(
    private route: ActivatedRoute,
    private conn: ConnectionService,
    public appConfig: AppConfig,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private broadcaster: Broadcaster,
    private fieldVisibilityService: FieldVisibilityService
  ) {
    this.multipleValueFields = [
      { title: 'Notes', fieldName: 'notesLanguageString' },
      { title: 'Restrictions', fieldName: 'restrictionsLanguageString' },
      { title: 'Warnings', fieldName: 'warningsLanguageString' },
      { title: 'Indication Restrictions (Not Visible to User)', fieldName: 'indicationRestrictionsLanguageString' },
      { title: 'Caution', fieldName: 'cautionsLanguageString' },
      { title: 'Side Effects', fieldName: 'sideEffectsLanguageString' },
      { title: 'Suitable in weather', fieldName: 'weathersLanguageString' },
    ];
  }

  async ngOnInit(): Promise<void> {
    this.regimens = [];
    this.showInactive = false;
    this.subscriptions = [];
    this.subscriptions = [];
    this.variantObj = [];
    this.sampleObj = [];
    this.restrictions = [];
    this.sideEffects = [];
    this.indicationRestrictions = [];
    this.user = ApiConnector.getCurrentUser();
    this.broadcaster.broadcast('ChatUserUpdate', { user: this.user });
    this.ui = { editModalOpenFlag: false, editProduct: undefined };
    await this.fieldVisibilityService.loadCatalogFieldsToHide();
    this.subscribeToUrlQueryParams();
  }

  subscribeToUrlQueryParams(): void {
    this.subscriptions.push(this.route.parent.params.subscribe(async () => {
      if (this.route.parent.snapshot.data.catalog) {
        const mainProduct = this.route.parent.snapshot.data.catalog;
        this.productObj = mainProduct;
        await this.conn.getCatalogs({ mainProduct: mainProduct.id, include: ['article'] })
          .then((data: any) => {
            this.data = data;
            data.map((item: any) => {
              if (item.get('type') === 'variant') this.variantObj.push(item);
              else if (item.get('type') === 'sample') this.sampleObj.push(item);
              return 0;
            });
          });
        const [tempData]: any = JSON.parse(JSON.stringify(this.data));
        if (tempData) {
          this.articles = tempData.article;
        }
        this.iframeLink = this.sanitizer.bypassSecurityTrustResourceUrl(
          `${this.conn.getWebAppUrl()}/article/${this.articles?.objectId}`);
        this.findProductRegimens();
        this.fetchFieldLanguageStrings('restrictions', this.data[0].get('restrictions'));
        this.fetchFieldLanguageStrings('sideEffects', this.data[0].get('sideEffects'));
        this.fetchFieldLanguageStrings('indicationRestrictions', this.data[0].get('indicationRestrictions'));
        await this.combosHavingThisProduct();
        await this.getArticleImage();
      }
    }));
  }

  async fetchFieldLanguageStrings(localFieldName: string, languageStringPointers: Array<any> = []): Promise<any> {
    this[localFieldName] = await this.conn.getLanguageStrings({ id: languageStringPointers.map((item: any) => item.id) });
  }

  async findProductRegimens(): Promise<any> {
    this.regimens = await this.conn.getCatalogRegimens(this.productObj);
  }

  saveResponse(responseFromModal: any): void {
    if (responseFromModal.info === 'SAVED'
      && responseFromModal.product.get('createdAt') === responseFromModal.product.get('updatedAt')) {
      if (responseFromModal.product.get('type') === 'variant') this.variantObj.push(responseFromModal.product);
      else this.sampleObj.push(responseFromModal.product);
    } else if (responseFromModal.err) alert(responseFromModal.err.message);
  }

  openCatalogVariantModal(product: any): void {
    const dialogRef = this.dialog.open(CatalogVariantModal, { data: { product } });
    this.subscriptions.push(dialogRef.afterClosed().subscribe((responseFromModal: any) => {
      if (!responseFromModal) return;
      this.saveResponse(responseFromModal);
    }));
  }

  addCatalog(type: string): void {
    const catalog = new Table.Catalog();
    const parentJson = JSON.parse(JSON.stringify(this.productObj));
    const excludeKeys = ['createdAt', 'updatedAt', 'objectId', 'mainProduct', 'mrp', 'price', 'quantity', 'article', 'articleId'];
    Object.keys(parentJson)
      .map((item: any) => {
        if (!excludeKeys.includes(item)) {
          catalog.set(item, this.productObj.get(item));
        }
        return 0;
      });
    catalog.set('type', type.toLowerCase());
    catalog.set('invoiceProduct', false);

    if (type === 'SAMPLE') {
      catalog.set('mrp', 0);
      catalog.set('price', 0);
    }
    catalog.set('mainProduct', this.productObj);
    this.openCatalogVariantModal(catalog);
  }

  async combosHavingThisProduct(): Promise<void> {
    const margId = this.productObj.get('margId');
    const escapedMargId = margId.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
    const regexPattern = `.*${escapedMargId}.*`;
    const payload: RequestQueryPayload<Table.Catalog> = {
      where: {
        margId: { $regex: regexPattern, $options: 'i' },
        objectId: { $ne: this.productObj.id },
        inventoryStatus: { $in: ['AVAILABLE', 'UNAVAILABLE'] },
      },
    };
    this.combos = await ApiConnector.find(Table.Catalog, payload);
  }

  addNewAddOnInstruction(): void {
    const dialogRef = this.dialog.open(CatalogAddOnInstructionEditModal, {
      data: { instruction: undefined, productObj: this.productObj }
    });
    this.subscriptions.push(dialogRef.afterClosed().subscribe(() => {
    }));
  }

  editAddOnInstruction(): void {
    const addOnInstruction = this.productObj.get('addOnInstruction');
    const dialogRef = this.dialog.open(CatalogAddOnInstructionEditModal, {
      data: { instruction: addOnInstruction, productObj: this.productObj }
    });
    this.subscriptions.push(dialogRef.afterClosed().subscribe(() => {
    }));
  }

  async deleteAddOnInstruction(): Promise<void> {
    try {
      await this.productObj.save({ addOnInstruction: undefined });
    } catch (error) {
      alert(error.message);
    }
  }

  async getArticleImage(): Promise<void> {
    const articleImagesLanguageString = this.productObj.get('article').get('params').articleImagesLanguageString;
    if (!articleImagesLanguageString || articleImagesLanguageString.length === 0) {
      return;
    }
    this.articleImageIds = articleImagesLanguageString.map((item: any) => item.id);
    this.selectedArticleImageIndex = 0;
    await this.loadArticleImage(0);
  }

  async loadArticleImage(index: number): Promise<void> {
    if (index < 0 || index >= this.articleImageIds.length) {
      return;
    }
    this.selectedArticleImageIndex = index;
    const articleId = this.articleImageIds[index];
    this.articleImageObj = await this.conn.getLanguageString(articleId);
    this.updateArticleImageSrc();
  }

  updateArticleImageSrc(): void {
    if (!this.articleImageObj) return;
    const imageUrl = this.articleImageObj.get(this.selectedLanguage) || this.articleImageObj.get('en');
    this.articleImageSrc = imageUrl || '';
  }

  onLanguageChange(): void {
    this.updateArticleImageSrc();
  }

  onArticleImageChange(): void {
    this.loadArticleImage(this.selectedArticleImageIndex);
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    delete this.route;
    delete this.conn;
    delete this.subscriptions;
    delete this.productObj;
    delete this.regimens;
  }
}
